import { useState, useRef } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronDown, Menu, X, Video, Sparkles, Zap, Star } from "lucide-react";
import logo from "@/assets/videoagentur-aachen-logo.png";
import ServiceSwitch from "@/components/ServiceSwitch";
import WebseitenBanner from "@/components/WebseitenBanner";
import { motion, AnimatePresence } from "framer-motion";

interface NavbarProps {
  variant?: "transparent" | "solid";
}

const Navbar = ({ variant = "transparent" }: NavbarProps) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [videoButtonHover, setVideoButtonHover] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ top: 0, right: 0 });
  const buttonRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  const handleVideoButtonHover = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPopupPosition({
        top: rect.bottom + 12,
        right: window.innerWidth - rect.right,
      });
    }
    setVideoButtonHover(true);
  };
  const isHomePage = location.pathname === "/";

  const scrollToSection = (id: string) => {
    if (isHomePage) {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    } else {
      window.location.href = `/#${id}`;
    }
    setMobileMenuOpen(false);
  };

  const navBackground = variant === "solid" 
    ? "bg-background/95 backdrop-blur-xl border-b border-border/50" 
    : "bg-white/10 backdrop-blur-xl border border-white/20 shadow-lg rounded-2xl";

  return (
    <nav className={`${variant === "solid" ? "absolute top-0 left-0 right-0" : "absolute top-4 md:top-6 left-0 right-0"} z-50 animate-fade-in`}>
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className={`relative flex items-center justify-between px-4 md:px-6 py-2 md:py-3 ${navBackground} overflow-visible`}>
          {/* Logo with Service Switch */}
          <div className="relative z-10 flex items-center gap-2 md:gap-3">
            <Link to="/" className="flex items-center gap-1 md:gap-2">
              <img src={logo} alt="Videoagentur Aachen - JC Productions" className="h-4 md:h-[22px]" width="88" height="22" loading="eager" decoding="async" fetchPriority="high" />
              <span className="text-[8px] md:text-xs text-foreground/40 font-light tracking-tight whitespace-nowrap">by JC Productions</span>
            </Link>
            <span className="text-foreground/20 text-lg font-light hidden md:inline">|</span>
            <ServiceSwitch />
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6">
            {/* Leistungen Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger className="text-foreground/70 hover:text-foreground transition-all duration-300 text-base tracking-wide relative group flex items-center gap-1 outline-none">
                <span className="relative z-10">Leistungen</span>
                <ChevronDown className="w-4 h-4" />
                <span className="absolute inset-0 -mx-3 -my-1 rounded-lg bg-white/0 group-hover:bg-white/10 transition-all duration-300"></span>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-card/95 backdrop-blur-xl border border-border/50 shadow-xl z-50">
                <DropdownMenuItem asChild>
                  <Link to="/leistungen/klassisch" className="cursor-pointer">
                    Klassische Produktion
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/leistungen/ki-produktionen" className="cursor-pointer">
                    KI-Produktionen
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Link
              to="/ueber-uns"
              className="text-foreground/70 hover:text-foreground transition-all duration-300 text-base tracking-wide relative group"
            >
              <span className="relative z-10">Über uns</span>
              <span className="absolute inset-0 -mx-3 -my-1 rounded-lg bg-white/0 group-hover:bg-white/10 transition-all duration-300"></span>
            </Link>

            <Link
              to="/referenzen"
              className="text-foreground/70 hover:text-foreground transition-all duration-300 text-base tracking-wide relative group"
            >
              <span className="relative z-10">Projekte</span>
              <span className="absolute inset-0 -mx-3 -my-1 rounded-lg bg-white/0 group-hover:bg-white/10 transition-all duration-300"></span>
            </Link>

            <Link
              to="/faq"
              className="text-foreground/70 hover:text-foreground transition-all duration-300 text-base tracking-wide relative group"
            >
              <span className="relative z-10">FAQ</span>
              <span className="absolute inset-0 -mx-3 -my-1 rounded-lg bg-white/0 group-hover:bg-white/10 transition-all duration-300"></span>
            </Link>

            {/* Video Project Button with Hover Popup */}
            <div 
              ref={buttonRef}
              className="relative"
              onMouseEnter={handleVideoButtonHover}
              onMouseLeave={() => setVideoButtonHover(false)}
            >
              <Link to="/termin">
                <Button
                  variant="ghost"
                  size="sm"
                  className="relative text-xs md:text-sm px-3 md:px-6 h-auto min-h-[2rem] md:min-h-[2.5rem] font-medium bg-accent/20 text-foreground border border-accent/40 transition-all duration-300 hover:bg-accent/30 hover:border-accent/60 hover:shadow-[0_0_25px_rgba(74,222,128,0.3)] active:scale-95 overflow-hidden"
                >
                  <Video className="w-4 h-4 mr-2" />
                  Videoprojekt starten
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent"
                    animate={{ x: ['-100%', '100%'] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                  />
                </Button>
              </Link>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-foreground/90 p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? "Menü schließen" : "Menü öffnen"}
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* WebseitenBanner - positioned below navbar on the right */}
        {isHomePage && (
          <div className="hidden md:flex justify-end mt-3 pr-6 relative z-[1]">
            <WebseitenBanner />
          </div>
        )}

        {/* Hover Popup rendered via Portal */}
        {createPortal(
          <AnimatePresence>
            {videoButtonHover && (
              <motion.div
                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.95 }}
                transition={{ duration: 0.2 }}
                style={{
                  position: 'fixed',
                  top: popupPosition.top,
                  right: popupPosition.right,
                  zIndex: 9999,
                }}
                className="w-72 p-4 bg-card/95 backdrop-blur-xl border border-accent/30 rounded-xl shadow-2xl shadow-accent/20"
                onMouseEnter={() => setVideoButtonHover(true)}
                onMouseLeave={() => setVideoButtonHover(false)}
              >
                <div className="absolute -top-2 right-8 w-4 h-4 bg-card/95 border-l border-t border-accent/30 rotate-45" />
                
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-accent" />
                  </div>
                  <div>
                    <p className="font-medium text-foreground">Filmproduktion starten</p>
                    <p className="text-xs text-muted-foreground">Real meets AI</p>
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Star className="w-3 h-3 text-accent" />
                    <span>Testimonials & Imagefilme</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Zap className="w-3 h-3 text-accent" />
                    <span>KI-Produktionen in 7 Tagen</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Video className="w-3 h-3 text-accent" />
                    <span>Social Media Content</span>
                  </div>
                </div>

                <div className="mt-3 pt-3 border-t border-border/50 text-xs text-accent flex items-center gap-1">
                  <span>Kostenlose Beratung</span>
                  <span className="animate-pulse">→</span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>,
          document.body
        )}

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden mt-2 px-4 py-4 bg-card/95 backdrop-blur-xl border border-border/50 rounded-2xl shadow-xl animate-fade-in">
            <div className="flex flex-col gap-4">
              <div className="border-b border-border/30 pb-2">
                <p className="text-sm text-muted-foreground mb-2">Leistungen</p>
                <Link
                  to="/leistungen/klassisch"
                  className="block text-foreground/80 hover:text-foreground py-2 pl-4 transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Klassische Produktion
                </Link>
                <Link
                  to="/leistungen/ki-produktionen"
                  className="block text-foreground/80 hover:text-foreground py-2 pl-4 transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  KI-Produktionen
                </Link>
              </div>

              <Link
                to="/ueber-uns"
                className="text-foreground/80 hover:text-foreground py-2 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Über uns
              </Link>

              <Link
                to="/referenzen"
                className="text-foreground/80 hover:text-foreground py-2 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Projekte
              </Link>

              <Link
                to="/faq"
                className="text-foreground/80 hover:text-foreground py-2 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                FAQ
              </Link>

              <Link to="/termin" onClick={() => setMobileMenuOpen(false)}>
                <Button
                  variant="hero"
                  className="mt-2 w-full font-bold"
                >
                  Projekt starten
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
